<c-container class="dashboard-container" fluid>
  <c-row class="mb-4">
    <c-col xs="12">
      <h2 class="mb-5">{{ 'dashboard_welcome' | translate: { appName: ('app.name' | translate) } }}</h2>
    </c-col>
  </c-row>

  <!-- User Level & eWallet -->
  @if (user(); as u) {
    <c-row class="mb-5">
      <c-col xs="12">
        <c-card class="level-card">
          <c-card-body>
            <!-- eWallet balance, per currency -->
            <div class="ewallet-balance d-flex align-items-center justify-content-between flex-wrap gap-2">
              <small class="text-muted mb-0">{{ 'dashboard_ewallet_balance' | translate }}</small>
              <div class="text-end">
                @if (nonZeroBalances(u); as bals) {
                  @for (b of bals; track b.currency) {
                    <h4 class="mb-0 text-success">{{ formatBalance(b.balance, b.currency) }}</h4>
                  } @empty {
                    <h4 class="mb-0 text-success">-</h4>
                  }
                }
              </div>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>

    <!-- KYC status blocks intentionally omitted for the Wolfpack Distribution app -->
  }

  <!-- Admin Fee Status -->
  @if (showAdminFeeStatus()) {
    <c-row class="mb-5">
      <c-col xs="12">
        @if (adminFeeExpired()) {
          <c-alert color="danger" [dismissible]="false" class="d-flex align-items-center">
            <svg cIcon name="cilBan" size="lg" class="me-3"></svg>
            <div class="flex-grow-1">
              <h5 class="alert-heading mb-2">{{ 'dashboard_admin_fee_expired_title' | translate }}</h5>
              <p class="mb-2">
                {{ 'dashboard_admin_fee_expired_message' | translate: { date: formatAdminFeeDate() } }}
              </p>
              <button
                cButton
                color="primary"
                size="sm"
                routerLink="/{{ currentLang() }}/order-wizard"
              >
                <svg cIcon name="cilCart" class="me-2"></svg>
                {{ 'dashboard_admin_fee_renew_button' | translate }}
              </button>
            </div>
          </c-alert>
        } @else if (adminFeeExpiringSoon()) {
          <c-alert color="warning" [dismissible]="false" class="d-flex align-items-center">
            <svg cIcon name="cilClock" size="lg" class="me-3"></svg>
            <div class="flex-grow-1">
              <h5 class="alert-heading mb-2">{{ 'dashboard_admin_fee_expiring_soon_title' | translate }}</h5>
              <p class="mb-2">
                {{ 'dashboard_admin_fee_expiring_soon_message' | translate: { date: formatAdminFeeDate() } }}
              </p>
              <button
                cButton
                color="dark"
                size="sm"
                routerLink="/{{ currentLang() }}/order-wizard"
              >
                <svg cIcon name="cilCart" class="me-2"></svg>
                {{ 'dashboard_admin_fee_renew_button' | translate }}
              </button>
            </div>
          </c-alert>
        } @else {
          <c-card class="admin-fee-card">
            <c-card-body>
              <div class="d-flex align-items-center">
                <svg cIcon name="cilCheck" size="lg" class="me-3 text-success"></svg>
                <div class="flex-grow-1">
                  <h5 class="mb-1">{{ 'dashboard_admin_fee_status' | translate }}</h5>
                  <p class="mb-0 text-muted">
                    {{ 'dashboard_admin_fee_paid_until' | translate }}:
                    <strong class="text-success">{{ formatAdminFeeDate() }}</strong>
                  </p>
                </div>
              </div>
            </c-card-body>
          </c-card>
        }
      </c-col>
    </c-row>
  }

  <!-- Referral Links -->
  @if (user(); as u) {
    <c-row class="mb-5">
      <c-col xs="12">
        <h4 class="referral-links-title mb-3">Referral Links</h4>

        <!-- FAC3LESS -->
        <h5 class="referral-link-name mb-2">FAC3LESS</h5>
        <app-referral-link class="d-block mb-4" [referralUrl]="u.referral_url"></app-referral-link>

        <!-- wolfpackglobalhealth -->
        <h5 class="referral-link-name mb-2">wolfpackglobalhealth</h5>
        <app-referral-link class="d-block mb-4" [referralUrl]="u.referral_url_wph"></app-referral-link>

        <!-- wolfpack-distribution -->
        <h5 class="referral-link-name mb-2">wolfpack-distribution</h5>
        <app-referral-link class="d-block mb-4" [referralUrl]="u.referral_url_wpd"></app-referral-link>

        <!-- wolfpack-assets -->
        <h5 class="referral-link-name mb-2">wolfpack-assets</h5>
        <c-card class="referral-link-card mb-4">
          <c-card-body class="d-flex align-items-center">
            <svg cIcon name="cilClock" class="me-2 text-muted"></svg>
            <span class="text-muted">Coming soon</span>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>
  }

  <!-- Cross-site single sign-on: jump to our other platforms with one click -->
  <c-row class="mb-4">
    <c-col xs="12">
      <app-cross-login></app-cross-login>
    </c-col>
  </c-row>

  <!-- My Pools -->
  <c-row class="mb-4">
    <c-col xs="12">
      <c-card>
        <c-card-header>
          <strong>{{ 'dashboard_my_pools_title' | translate }}</strong>
        </c-card-header>
        <c-card-body>
          @if (poolsLoading()) {
            <div class="text-center py-4">
              <c-spinner color="primary"></c-spinner>
              <p class="mt-2">{{ 'dashboard_pools_loading' | translate }}</p>
            </div>
          }
          @if (!poolsLoading() && userPools().length === 0) {
            <div class="alert alert-info">
              {{ 'dashboard_no_pools' | translate }}
            </div>
          }
          @if (!poolsLoading() && userPools().length > 0) {
            <div class="table-responsive">
              <table cTable striped hover>
                <thead>
                  <tr>
                    <th>{{ 'dashboard_pool_id' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_money_received' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_max_payout' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_percentage' | translate }}</th>
                    <th class="text-center">{{ 'user_pools.email_ok' | translate }}</th>
                    <th>{{ 'dashboard_started' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (pool of userPools(); track pool.id) {
                    <tr>
                      <td>
                        <span class="badge bg-info">
                          {{ 'dashboard_pool' | translate }} {{ pool.pool_id }}
                        </span>
                      </td>
                      <td class="text-end">{{ formatCurrency(pool.turnover) }}</td>
                      <td class="text-end">{{ formatCurrency(pool.max_balance) }}</td>
                      <td class="text-end">
                        <strong>{{ getPercentage(pool) }}%</strong>
                      </td>
                      <td class="text-center">
                        @if (!pool.needs_email) {
                          <span class="text-body-secondary">
                            {{ 'user_pools.email_not_required' | translate }}
                          </span>
                        } @else if (pool.has_emails) {
                          <span class="text-success fw-bold">OK</span>
                        } @else {
                          <span class="text-danger fw-bold">
                            {{ 'user_pools.email_not_ok' | translate }}
                          </span>
                        }
                      </td>
                      <td>{{ formatDate(pool.created_at) }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            @if (hasPoolsWithEmailNotOk()) {
              <c-alert color="warning" [dismissible]="false" class="d-flex align-items-center mt-3">
                <svg cIcon name="cilExclamation" size="lg" class="me-3"></svg>
                <div class="flex-grow-1">
                  <strong>{{ 'dashboard_pools_email_required_title' | translate }}</strong>
                  <p class="mb-0">
                    {{ 'dashboard_pools_email_required_message' | translate }}
                  </p>
                  <p class="mb-0 mt-2">
                    <strong>{{ 'dashboard_pools_email_status' | translate: { confirmed: confirmedEmailsCount(), required: requiredEmailsCount() } }}</strong>
                  </p>
                </div>
              </c-alert>
            }
          }
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>

  <!-- Community Widget -->
  <c-row class="mb-4">
    <c-col xs="12">
      <app-community-dashboard-widget></app-community-dashboard-widget>
    </c-col>
  </c-row>

  <!-- License Countdown -->
  @if (!presaleEnded() && domainId() === 0 && lic1Active()) {
    <c-row class="mb-4">
      <c-col xs="12">
        <c-card class="license-countdown-card">
          <c-card-body class="text-center py-4">
            <h4 class="license-countdown-title mb-3">
              <svg cIcon name="cilClock" class="me-2"></svg>
              {{ 'license_countdown_title' | translate }}
            </h4>
            <p class="license-countdown-message mb-4">
              {{ 'license_countdown_message' | translate }}
            </p>
            <div class="countdown-container">
              <div class="countdown-display">
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownDays() }}</span>
                  <span class="countdown-label">{{ 'days' | translate }}</span>
                </div>
                <span class="countdown-separator">:</span>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownHours() }}</span>
                  <span class="countdown-label">{{ 'hours' | translate }}</span>
                </div>
                <span class="countdown-separator">:</span>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownMinutes() }}</span>
                  <span class="countdown-label">{{ 'minutes' | translate }}</span>
                </div>
                <span class="countdown-separator">:</span>
                <div class="countdown-item">
                  <span class="countdown-value">{{ countdownSeconds() }}</span>
                  <span class="countdown-label">{{ 'seconds' | translate }}</span>
                </div>
              </div>
              <p class="deadline-date mt-3 mb-1">{{ 'license_countdown_deadline' | translate }}</p>
              <p class="timezone-info mb-0">{{ 'timezone_atlanta' | translate }}</p>
            </div>
          </c-card-body>
        </c-card>
      </c-col>
    </c-row>
  }

  <!-- Latest Info Media -->
  @if (media(); as m) {
    <div class="info-media-widget">
      <c-row class="mb-4">
        <c-col>
          <media-card [media]="m" (click)="openMediaViewer(m)"></media-card>
        </c-col>
      </c-row>
      <c-row [routerLink]="['/', currentLang(), 'media', 'info']" class="mb-4">
        <c-col>
          <c-alert color="success" class="info">
            <svg cIcon name="info" class="me-2"></svg>
            <span [translate]="'menu.info'"></span>
          </c-alert>
        </c-col>
      </c-row>
    </div>
  }

  <!-- PDF Documentation Links
  <c-row class="mb-5">
    <c-col xs="12">
      <c-card class="info-links-card">
        <c-card-body>
          <h4 class="mb-3">{{ 'dashboard_documentation_title' | translate }}</h4>
          <c-row>
            <c-col xs="12">
              <button
                cButton
                color="primary"
                variant="outline"
                class="w-100"
                (click)="openPdfModal('provisions')"
              >
                <svg cIcon name="cilFile" class="me-2"></svg>
                {{ 'dashboard_pdf_provisions_link' | translate }}
              </button>
            </c-col>
          </c-row>
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>

  <!-- Sponsored Users Statistics -->
  <c-row class="mb-4">
    <c-col xs="12">
      <c-card>
        <c-card-header>
          <strong>{{ 'dashboard_sponsored_users_title' | translate }}</strong>
        </c-card-header>
        <c-card-body>
          @if (loading()) {
            <div class="text-center py-4">
              <c-spinner color="primary"></c-spinner>
              <p class="mt-2">{{ 'dashboard_sponsored_loading' | translate }}</p>
            </div>
          }
          @if (error() && !loading()) {
            <div class="alert alert-danger">
              {{ error() }}
            </div>
          }
          @if (stats() && !loading()) {
            <div class="table-responsive">
              <table cTable striped hover>
                <thead>
                  <tr>
                    <th>{{ 'dashboard_sponsored_period' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_sponsored_active_users' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_sponsored_inactive_users' | translate }}</th>
                    <th class="text-end">{{ 'dashboard_sponsored_unverified_users' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>{{ 'dashboard_sponsored_total' | translate }}</strong></td>
                    <td class="text-end"><strong>{{ stats()!.total.active }}</strong></td>
                    <td class="text-end"><strong>{{ stats()!.total.inactive }}</strong></td>
                    <td class="text-end"><strong>{{ stats()!.total.unverified }}</strong></td>
                  </tr>
                  <tr>
                    <td>{{ 'dashboard_sponsored_last_90_days' | translate }}</td>
                    <td class="text-end">{{ stats()!.day_90.active }}</td>
                    <td class="text-end">{{ stats()!.day_90.inactive }}</td>
                    <td class="text-end">{{ stats()!.day_90.unverified }}</td>
                  </tr>
                  <tr>
                    <td>{{ 'dashboard_sponsored_last_30_days' | translate }}</td>
                    <td class="text-end">{{ stats()!.day_30.active }}</td>
                    <td class="text-end">{{ stats()!.day_30.inactive }}</td>
                    <td class="text-end">{{ stats()!.day_30.unverified }}</td>
                  </tr>
                  <tr>
                    <td>{{ 'dashboard_sponsored_last_7_days' | translate }}</td>
                    <td class="text-end">{{ stats()!.day_7.active }}</td>
                    <td class="text-end">{{ stats()!.day_7.inactive }}</td>
                    <td class="text-end">{{ stats()!.day_7.unverified }}</td>
                  </tr>
                  <tr>
                    <td>{{ 'dashboard_sponsored_last_24_hours' | translate }}</td>
                    <td class="text-end">{{ stats()!.day_1.active }}</td>
                    <td class="text-end">{{ stats()!.day_1.inactive }}</td>
                    <td class="text-end">{{ stats()!.day_1.unverified }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          }
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>

  <!-- Quick Action Cards -->
  <c-row>
    <c-col xs="12" md="6" lg="4" class="mb-4">
      <c-card class="dashboard-card h-100">
        <c-card-body class="d-flex flex-column">
          <div class="card-icon mb-3">
            <svg cIcon name="cilCart" size="lg"></svg>
          </div>
          <h3 cCardTitle>{{ 'dashboard_order_products_title' | translate }}</h3>
          <p cCardText class="flex-grow-1">
            {{ 'dashboard_order_products_description' | translate }}
          </p>
          <button
            cButton
            color="primary"
            size="lg"
            class="w-100"
            routerLink="/{{ currentLang() }}/order-wizard"
          >
            <svg cIcon name="cilCart" class="me-2"></svg>
            {{ 'dashboard_start_order' | translate }}
          </button>
        </c-card-body>
      </c-card>
    </c-col>

    <c-col xs="12" md="6" lg="4" class="mb-4">
      <c-card class="dashboard-card h-100">
        <c-card-body class="d-flex flex-column">
          <div class="card-icon mb-3">
            <svg cIcon name="cilUser" size="lg"></svg>
          </div>
          <h3 cCardTitle>{{ 'dashboard_my_profile_title' | translate }}</h3>
          <p cCardText class="flex-grow-1">
            {{ 'dashboard_my_profile_description' | translate }}
          </p>
          <button
            cButton
            color="success"
            variant="outline"
            size="lg"
            class="w-100"
            routerLink="/{{ currentLang() }}/profile"
          >
            <svg cIcon name="cilUser" class="me-2"></svg>
            {{ 'dashboard_view_profile' | translate }}
          </button>
        </c-card-body>
      </c-card>
    </c-col>

    <c-col xs="12" md="6" lg="4" class="mb-4">
      <c-card class="dashboard-card h-100">
        <c-card-body class="d-flex flex-column">
          <div class="card-icon mb-3">
            <svg cIcon name="cilFile" size="lg"></svg>
          </div>
          <h3 cCardTitle>{{ 'dashboard_my_orders_title' | translate }}</h3>
          <p cCardText class="flex-grow-1">
            {{ 'dashboard_my_orders_description' | translate }}
          </p>
          <button
            cButton
            color="primary"
            variant="outline"
            size="lg"
            class="w-100"
            [routerLink]="['/', currentLang(), 'my-orders']"
          >
            <svg cIcon name="cilFile" class="me-2"></svg>
            {{ 'dashboard_view_orders' | translate }}
          </button>
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>
</c-container>